import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import CommerceCategory from 'types/models/CommerceCategory';
import PaginationData from 'types/models/PaginationData';
import EditCommerceCategoriesContext from 'contexts/EditCommerceCategoriesContext';
import { getCommerceCategoriesWithPagination } from 'services/commerceServices';
import AppContext from 'contexts/AppContext';
import SearchBar from 'components/SearchBar';
import useDebounce from 'hooks/useDebounce';

export default (): JSX.Element => {
  const history = useHistory();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const [paginationCommerceCategory, setPaginationCommerceCategory] =
    React.useState<PaginationData<CommerceCategory>>();
  const [searchValue, setSearchValue] = React.useState('');
  const { setSelectedCommerceCategory } = React.useContext(EditCommerceCategoriesContext);
  const { t } = useTranslation();

  const loadCommerceCategories = React.useCallback(async (page?: number, searchTerm?: string) => {
    const paginationData = await getCommerceCategoriesWithPagination(page, searchTerm);
    if (paginationData) {
      setPaginationCommerceCategory(paginationData);
    }
  }, []);

  useDebounce(() => {
    if (searchValue.trim()) {
      loadCommerceCategories(undefined, searchValue);
    } else {
      loadCommerceCategories();
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (currentPageListNumber) {
      loadCommerceCategories(currentPageListNumber);
    }
  }, [currentPageListNumber]);
  const renderEditButton = (commerceCategory: CommerceCategory): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedCommerceCategory(commerceCategory);
          history.push('commerce-categories/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: CommerceCategory[]): JSX.Element => {
    return (
      <>
        {paginationData.map((commerceCategory: CommerceCategory) => (
          <TableRow key={commerceCategory.id}>
            <TableCell component="th" scope="row">
              {commerceCategory.description}
            </TableCell>
            <TableCell>{renderEditButton(commerceCategory)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <SearchBar
        label={t('category-search-label')}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onClear={() => {
          setSearchValue('');
        }}
      />

      <Button
        className="btn-50"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('/private/commerce-categories/create');
        }}
      >
        {t('listCommerceCategories-page-create')}
      </Button>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listCommerceCategories-page-description')}</TableCell>
              <TableCell>{t('listCommerceCategories-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationCommerceCategory?.data && paginationCommerceCategory?.data.length > 0 ? (
              renderContent(paginationCommerceCategory.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listCommerceCategories-page-noCategories')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationCommerceCategory && (
        <TablePagination
          component="div"
          count={paginationCommerceCategory.total}
          page={paginationCommerceCategory.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationCommerceCategory.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
